// Opens Locked with its passphrase, for this tab alone and until it reseals. A wrong passphrase
// says only that it is wrong and that Locked stays sealed.
//
// A passphrase that is forgotten can't be recovered — nothing but it opens what Locked holds — so
// the way out offered here is resetting Locked, which throws everything in it away.
import PropTypes from 'prop-types'
import {useMutation} from '@apollo/client/react'
import {useState} from 'react'

import Dialog from '@mui/material/Dialog'
import ShieldIcon from '@mui/icons-material/ShieldOutlined'

import PassphraseField from './PassphraseField'
import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {SEALED, says} from '../../lib/errorTools'
import {UNLOCK_LOCKED} from '../../graphql/mutations/locked'
import {openLocked} from '../../lib/lockedTools'

import './lockedDialog.scss'

const minutes = seconds => `${Math.round(seconds / 60)} minute${Math.round(seconds / 60) === 1 ? '' : 's'}`

const UnlockDialog = ({resealAfter, reason = null, onClose, onUnlocked, onForgot}) => {
  const [passphrase, setPassphrase] = useState('')
  const [failure, setFailure] = useState(null)
  const [unlock, {loading}] = useMutation(UNLOCK_LOCKED)

  const handleSubmit = event => {
    event.preventDefault()
    setFailure(null)

    unlock({variables: {input: {passphrase}}})
      .then(({data}) => {
        openLocked(data.unlockLocked)
        onUnlocked()
      })
      .catch(error => setFailure(says(error, SEALED) ? 'wrong' : 'failed'))
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby='unlock-title'
      slotProps={{paper: {className: 'locked-dialog', component: 'form', onSubmit: handleSubmit}}}>
      <div className='dialog-head'>
        <ShieldIcon />
        <h2 className='dialog-title' id='unlock-title'>Unlock Locked</h2>
      </div>
      <Text className='dialog-body'>
        {reason ?? `Enter your passphrase. Locked reseals itself after ${minutes(resealAfter)} without use.`}
      </Text>

      <PassphraseField label='Passphrase' value={passphrase} onChange={setPassphrase} autoComplete='current-password' autoFocus />
      {failure === 'wrong' && <Text className='dialog-error' role='alert'>That is not the passphrase. Locked stays sealed.</Text>}
      {failure === 'failed' && <Text className='dialog-error' role='alert'>Locked couldn’t be opened. Try again.</Text>}

      <button type='button' className='dialog-link' onClick={onForgot}>Forgot your passphrase?</button>

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton className='dialog-confirm' color='secondary' type='submit' disabled={loading || !passphrase}>
          {loading ? 'Unlocking…' : 'Unlock'}
        </BaseButton>
      </div>
    </Dialog>
  )
}

UnlockDialog.propTypes = {
  // Seconds without use before Locked reseals itself.
  resealAfter: PropTypes.number.isRequired,
  // Why Locked is being asked for, when it is on the way to something else.
  reason: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onUnlocked: PropTypes.func.isRequired,
  onForgot: PropTypes.func.isRequired
}

export default UnlockDialog
